"use client";

import { useEffect, useId, useRef, useState } from "react";
import { initialen } from "./auswahl-konfiguration";
import { erwaehnungsAbfrage, filtereErwaehnbare, serialisiereErwaehnungen, type ErwaehnbarePerson } from "./composer-logik";
import { Knopf } from "../knopf";
import { Mehrzeiler } from "./mehrzeiler";

/**
 * Composer (Musterkatalog M-011 AD6, Design-Guide `composer.md`): Eingabe für einen Kommentar mit @-Liste. Das Feld ist
 * ein `Mehrzeiler` (Feld-Hülle, Label für Hilfstechnik, Hinweissatz am Feld). Tippt man `@`, erscheint die Liste der
 * erwähnbaren Personen; sie ist still nach Name gefiltert und enthält nur, was der Aufrufer geliefert hat (Anbieten ist nie
 * großzügiger als Erlauben, r110: der Aufrufer füllt sie aus derselben Quelle, nach der die Action Erwähnungen annimmt).
 * Wahl per Maus, Pfeiltasten + Eingabe/Tab; Esc schließt zuerst die Liste (`data-esc-eigen`), nicht den umgebenden Dialog.
 * Senden: Knopf oder Strg/Cmd+Enter. Scheitert das Senden, bleibt der Text stehen und der Satz steht am Feld (r98/r105).
 * Der Baustein prüft keine Rechte; ohne Schreibrecht rendert der Aufrufer ihn gar nicht.
 */
export type ComposerErgebnis = { ok: true } | { ok: false; meldung: string };

const LISTE = "absolute bottom-full inset-x-0 mb-1 z-20 max-h-48 overflow-y-auto bg-dropdown border border-dropdown-line rounded-lg shadow-md p-1";
const EINTRAG_AKTIV = "w-full flex items-center gap-x-3 py-2 px-3 rounded-lg text-sm text-start text-dropdown-item-foreground bg-dropdown-item-hover";
const EINTRAG = "w-full flex items-center gap-x-3 py-2 px-3 rounded-lg text-sm text-start text-dropdown-item-foreground hover:bg-dropdown-item-hover";

export function Composer({
  label,
  platzhalter,
  eigenerName,
  personen,
  onSenden,
  fokusSignal = 0,
  vorbelegung,
}: {
  label: string;
  platzhalter: string;
  /** Name der schreibenden Person (Initialen als leitender Avatar). */
  eigenerName: string;
  personen: ErwaehnbarePerson[];
  onSenden: (eingabe: { text: string; erwaehnte: string[] }) => Promise<ComposerErgebnis>;
  /** Erhöht der Aufrufer diese Zahl, bekommt das Feld den Fokus (z. B. „Frage stellen“). */
  fokusSignal?: number;
  /** Text, der beim Fokus-Signal in ein leeres Feld gesetzt wird (z. B. „@Marta Weiß “). */
  vorbelegung?: string;
}) {
  const basis = useId().replace(/:/g, "");
  const huelle = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [cursor, setCursor] = useState(0);
  const [aktiv, setAktiv] = useState(0);
  const [geschlossen, setGeschlossen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<ErwaehnbarePerson[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | undefined>();

  const textfeld = () => huelle.current?.querySelector("textarea") ?? null;

  const abfrage = erwaehnungsAbfrage(text, cursor);
  const treffer = abfrage ? filtereErwaehnbare(personen, abfrage.suche) : [];
  const listeOffen = !geschlossen && abfrage !== null && treffer.length > 0;
  const listenId = `${basis}-liste`;

  // Fokus-Signal von außen (Handlungskarte „Frage stellen“ / „Nachfragen“).
  const letztesSignal = useRef(fokusSignal);
  useEffect(() => {
    if (fokusSignal === letztesSignal.current) return;
    letztesSignal.current = fokusSignal;
    if (vorbelegung && text === "") {
      setText(vorbelegung);
      setCursor(vorbelegung.length);
    }
    textfeld()?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fokusSignal]);

  const waehle = (p: ErwaehnbarePerson) => {
    if (!abfrage) return;
    const davor = text.slice(0, abfrage.start);
    const danach = text.slice(cursor);
    const neu = `${davor}@${p.name} ${danach}`;
    const pos = davor.length + p.name.length + 2;
    setText(neu);
    setCursor(pos);
    setGewaehlt((g) => (g.some((x) => x.id === p.id) ? g : [...g, p]));
    setAktiv(0);
    setGeschlossen(false);
    requestAnimationFrame(() => {
      const t = textfeld();
      if (t) {
        t.focus();
        t.setSelectionRange(pos, pos);
      }
    });
  };

  const senden = async () => {
    const roh = text.trim();
    if (!roh || laeuft) return;
    const { text: gespeichert, erwaehnte } = serialisiereErwaehnungen(roh, gewaehlt);
    setLaeuft(true);
    setMeldung(undefined);
    try {
      const r = await onSenden({ text: gespeichert, erwaehnte });
      if (r.ok) {
        setText("");
        setCursor(0);
        setGewaehlt([]);
      } else {
        setMeldung(r.meldung);
      }
    } catch {
      setMeldung("Der Kommentar konnte nicht gesendet werden. Bitte erneut versuchen.");
    } finally {
      setLaeuft(false);
    }
  };

  const beiTaste = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (listeOffen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setAktiv((i) => (i + 1) % treffer.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setAktiv((i) => (i - 1 + treffer.length) % treffer.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        waehle(treffer[Math.min(aktiv, treffer.length - 1)]!);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setGeschlossen(true);
        return;
      }
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      void senden();
    }
  };

  return (
    <>
      {/* Preline: component/textarea/textarea-label */}
      <div className="relative flex items-start gap-x-3" ref={huelle}>
        <span className="mt-1 flex shrink-0 justify-center items-center size-8 bg-layer border border-layer-line text-xs font-semibold uppercase text-layer-foreground rounded-full" aria-hidden="true">
          {initialen(eigenerName)}
        </span>
        <div className="grow min-w-0">
          {listeOffen && (
            <ul id={listenId} role="listbox" aria-label="Personen erwähnen" className={LISTE}>
              {treffer.map((p, i) => (
                <li key={p.id} role="presentation">
                  <button
                    type="button"
                    role="option"
                    id={`${basis}-opt-${i}`}
                    aria-selected={i === aktiv}
                    tabIndex={-1}
                    className={i === aktiv ? EINTRAG_AKTIV : EINTRAG}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => waehle(p)}
                  >
                    <span className="inline-flex shrink-0 items-center justify-center size-6 rounded-full bg-surface text-xs font-medium text-surface-foreground" aria-hidden="true">
                      {initialen(p.name)}
                    </span>
                    {p.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Mehrzeiler
            label={label}
            labelVersteckt
            groesse="kompakt"
            rows={2}
            placeholder={platzhalter}
            value={text}
            disabled={laeuft}
            hinweissatz={meldung}
            role="combobox"
            aria-expanded={listeOffen}
            aria-controls={listeOffen ? listenId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={listeOffen ? `${basis}-opt-${Math.min(aktiv, treffer.length - 1)}` : undefined}
            data-esc-eigen={listeOffen ? "offen" : undefined}
            onChange={(e) => {
              setText(e.target.value);
              setCursor(e.target.selectionStart);
              setAktiv(0);
              setGeschlossen(false);
            }}
            onSelect={(e) => setCursor(e.currentTarget.selectionStart)}
            onKeyDown={beiTaste}
          />
          <div className="mt-2 flex items-center justify-between gap-x-3">
            <span className="text-xs text-muted-foreground-1">@ erwähnt eine Person · Strg+Enter sendet</span>
            <Knopf variante="primaer" starr onClick={() => void senden()} disabled={text.trim() === ""} laedt={laeuft}>
              {laeuft ? "Sendet …" : "Senden"}
            </Knopf>
          </div>
        </div>
      </div>
    </>
  );
}
